<template>
    <input
        type="text"
        v-bind="$attrs"
        class="form-input"
        :class="inputClassName"
        :disabled="disabled"
        :readonly="readonly"
        ref="inputRef"
        @input="handleInput"
        @focus="handleFocus"
        @blur="handleBlur"
        @click="$emit('click')"
        @keydown="handleKeydown"
        v-model="localValue"
    />
</template>

<script setup lang="ts">
    import { computed, nextTick, ref, watch, useAttrs } from 'vue';
    import { VueClass } from '@/types/Type.ts';

    const props = withDefaults(
        defineProps<{
            allowNegativeValue?: boolean;
            decimalSymbol?: string;
            disabled?: boolean;
            inputClassName?: VueClass;
            precision?: number;
            readonly?: boolean;
            thousandsSeparator?: string;
            value?: any;
            formatLocale?: string;
        }>(),
        {
            allowNegativeValue: false,
            decimalSymbol: '.',
            disabled: false,
            inputClassName: '',
            precision: 2,
            readonly: false,
            thousandsSeparator: ',',
            value: '',
            formatLocale: 'en-CA',
        }
    );

    const emit = defineEmits<{
        (e: 'input', value: number | null): void;
        (e: 'change', value: number | null): void;
        (e: 'focus', event: FocusEvent): void;
        (e: 'blur', event: FocusEvent): void;
        (e: 'click'): void;
    }>();

    useAttrs(); // expose $attrs for v-bind

    const inputRef = ref<HTMLInputElement | null>(null);
    const localValue = ref<string>('');
    const isFocused = ref(false);
    const syncingFromValue = ref(false);

    const resolvedDecimalSymbol = computed(() => props.decimalSymbol || '.');
    const resolvedThousandsSeparator = computed(() => props.thousandsSeparator ?? ',');

    const numberFormatter = computed(
        () =>
            new Intl.NumberFormat(props.formatLocale, {
                useGrouping: true,
                minimumFractionDigits: props.precision,
                maximumFractionDigits: props.precision,
            })
    );

    const actualGroupingSeparator = computed(() => {
        const sample = numberFormatter.value.format(1000);
        return sample.replaceAll(/\d/g, '') || '';
    });

    const actualDecimalSeparator = computed(() => {
        const sample = numberFormatter.value.format(1.1);
        const match = sample.match(/1(.*)1/);
        return match ? match[1] : '.';
    });

    const detectDecimalChar = (rawValue: string): string | null => {
        const lastSeparatorMatch = rawValue.match(/[.,](?!.*[.,])/);

        if (!lastSeparatorMatch || lastSeparatorMatch.index === undefined) {
            return null;
        }

        const candidate = lastSeparatorMatch[0];
        const trailing = rawValue.slice(lastSeparatorMatch.index + 1);
        const trailingIsDigits = trailing !== '' && /^\d+$/.test(trailing);

        if (
            candidate === resolvedThousandsSeparator.value &&
            trailingIsDigits &&
            trailing.length === 3
        ) {
            return null;
        }

        return trailingIsDigits ? candidate : null;
    };

    type SanitizeState = {
        sanitized: string;
        hasDecimal: boolean;
        hasSign: boolean;
        decimalsCount: number;
        chosenDecimalChar: string;
        thousandsSeparator: string;
        decimalIndex: number;
    };

    const createSanitizeState = (rawValue: string): SanitizeState => {
        const chosenDecimalChar = detectDecimalChar(rawValue) || resolvedDecimalSymbol.value;

        return {
            sanitized: '',
            hasDecimal: false,
            hasSign: false,
            decimalsCount: 0,
            chosenDecimalChar,
            thousandsSeparator: resolvedThousandsSeparator.value,
            decimalIndex: rawValue.lastIndexOf(chosenDecimalChar),
        };
    };

    const shouldAddSign = (char: string, state: SanitizeState): boolean =>
        char === '-' && !state.hasSign && state.sanitized.length === 0;

    const shouldSkipThousands = (char: string, state: SanitizeState): boolean =>
        Boolean(
            state.thousandsSeparator &&
                char === state.thousandsSeparator &&
                char !== state.chosenDecimalChar
        );

    const isDecimalChar = (char: string, chosenDecimalChar: string): boolean =>
        char === chosenDecimalChar || char === '.' || char === ',';

    const appendDecimalIfNeeded = (char: string, index: number, state: SanitizeState): boolean => {
        if (!isDecimalChar(char, state.chosenDecimalChar)) {
            return false;
        }

        const isChosenPosition = state.decimalIndex >= 0 ? index === state.decimalIndex : true;

        if (state.hasDecimal || !isChosenPosition) {
            return true;
        }

        state.hasDecimal = true;
        state.sanitized += state.chosenDecimalChar;

        return true;
    };

    const appendSignIfNeeded = (char: string, state: SanitizeState): boolean => {
        if (!shouldAddSign(char, state)) {
            return false;
        }

        state.hasSign = true;
        state.sanitized += '-';

        return true;
    };

    const appendDigitIfValid = (char: string, state: SanitizeState) => {
        if (!/\d/.test(char)) {
            return;
        }

        if (state.hasDecimal && state.decimalsCount >= props.precision) {
            return;
        }

        if (state.hasDecimal) {
            state.decimalsCount += 1;
        }

        state.sanitized += char;
    };

    const sanitizeInput = (rawValue: string): string => {
        const state = createSanitizeState(rawValue);

        for (let index = 0; index < rawValue.length; index += 1) {
            const char = rawValue[index];

            if (appendSignIfNeeded(char, state)) {
                continue;
            }

            if (shouldSkipThousands(char, state)) {
                continue;
            }

            if (appendDecimalIfNeeded(char, index, state)) {
                continue;
            }

            appendDigitIfValid(char, state);
        }

        return state.sanitized === '-' ? '' : state.sanitized;
    };

    /**
     * Central parse:
     * - preserve the last decimal-like char as decimal
     * - drop other thousands separators
     * - normalize decimal to '.'
     * This preserves the behavior that "152,21" with decimalSymbol="."
     * is parsed as 152.21 even though ',' is also configured as thousands sep.
     */
    const resolveDecimalCharacter = (rawValue: string): string | null => {
        const detected = detectDecimalChar(rawValue);

        if (detected) {
            return detected;
        }

        const configuredDecimal = resolvedDecimalSymbol.value;

        if (configuredDecimal !== '.' && rawValue.includes(configuredDecimal)) {
            return configuredDecimal;
        }

        return null;
    };

    const normalizeWithSeparators = (
        value: string,
        decimalChar: string | null,
        thousandsSeparator: string
    ): string => {
        if (!decimalChar) {
            return thousandsSeparator ? value.split(thousandsSeparator).join('') : value;
        }

        const lastIndex = value.lastIndexOf(decimalChar);

        return value.split('').reduce((result, ch, index) => {
            if (index === lastIndex) {
                return `${result}.`;
            }

            if (thousandsSeparator && ch === thousandsSeparator) {
                return result;
            }

            return `${result}${ch}`;
        }, '');
    };

    const parseUserInputToNumeric = (rawValue: string): number | null => {
        if (!rawValue) return null;

        const thousandsSeparator = resolvedThousandsSeparator.value;
        const decimalChar = resolveDecimalCharacter(rawValue);
        const normalized = normalizeWithSeparators(rawValue, decimalChar, thousandsSeparator);
        const numeric = Number.parseFloat(normalized);

        if (Number.isNaN(numeric) || (!props.allowNegativeValue && numeric < 0)) {
            return null;
        }

        return numeric;
    };

    const normalizeNumericValue = (rawValue: unknown): number | null => {
        if (rawValue === null || rawValue === undefined || rawValue === '') {
            return 0;
        }

        if (typeof rawValue === 'number') {
            return Number.isFinite(rawValue) ? Number(rawValue) : null;
        }

        if (typeof rawValue === 'string') {
            if (!/\d/.test(rawValue)) {
                return null;
            }

            return parseUserInputToNumeric(rawValue);
        }

        return null;
    };

    const formatNumber = (numericValue: number | null): string => {
        if (numericValue === null) {
            return '';
        }

        let formatted = numberFormatter.value.format(numericValue);

        if (actualGroupingSeparator.value !== resolvedThousandsSeparator.value) {
            formatted = formatted
                .split(actualGroupingSeparator.value)
                .join(resolvedThousandsSeparator.value);
        }

        if (resolvedThousandsSeparator.value) {
            formatted = formatted.replaceAll(/\s/g, resolvedThousandsSeparator.value);
        }

        if (actualDecimalSeparator.value !== resolvedDecimalSymbol.value) {
            formatted = formatted.replace(
                actualDecimalSeparator.value,
                resolvedDecimalSymbol.value
            );
        }

        return formatted;
    };

    const standardizeEmitValue = (numericValue: number | null): number | null => {
        if (numericValue === null || Number.isNaN(numericValue)) {
            return null;
        }

        return Number.parseFloat(Number(numericValue).toFixed(props.precision));
    };

    const applyFormattedFromNumeric = (numericValue: number | null) => {
        const displayValue = numericValue === null ? 0 : numericValue;

        syncingFromValue.value = true;
        localValue.value = formatNumber(displayValue);
        nextTick(() => {
            syncingFromValue.value = false;
        });
    };

    const syncFromValue = (rawValue: unknown) => {
        if (isFocused.value) {
            return;
        }

        const numericValue = normalizeNumericValue(rawValue);
        applyFormattedFromNumeric(numericValue);
    };

    const handleInput = (event: Event) => {
        const raw = (event?.target as HTMLInputElement | undefined)?.value ?? '';
        const sanitized = sanitizeInput(raw);

        if (sanitized !== localValue.value) {
            localValue.value = sanitized;
        }
    };

    const handleFocus = (event: FocusEvent) => {
        isFocused.value = true;
        emit('focus', event);
    };

    const handleBlur = (event: FocusEvent) => {
        isFocused.value = false;
        emit('blur', event);

        const previousNumericValue = normalizeNumericValue(props.value);
        const displayBefore = localValue.value;

        const numericValue = normalizeNumericValue(localValue.value);
        const coercedValue = numericValue === null ? 0 : numericValue;

        const formattedCoerced = formatNumber(coercedValue);

        // Update local display with syncing guard
        syncingFromValue.value = true;
        localValue.value = formattedCoerced;
        nextTick(() => {
            syncingFromValue.value = false;
        });

        const emitValue = standardizeEmitValue(coercedValue);
        const displayChanged = displayBefore !== formattedCoerced;

        if (displayChanged || coercedValue !== previousNumericValue) {
            emit('input', emitValue);
            emit('change', emitValue);
        }
    };

    const handleKeydown = (event: KeyboardEvent) => {
        const key = event.key;
        if (key !== '.' && key !== ',') {
            return;
        }

        const input = event.target as HTMLInputElement;
        const value = input.value;
        const selectionStart = input.selectionStart ?? 0;
        const selectionEnd = input.selectionEnd ?? selectionStart;

        // What the user *sees* as decimal in the field
        const decimalSymbol = resolvedDecimalSymbol.value;
        const existingDecimalIndex = value.indexOf(decimalSymbol);

        // No existing decimal → let sanitizeInput handle where to put it
        if (existingDecimalIndex === -1) {
            return;
        }

        // If the selection covers the existing decimal, allow replacing it
        const selectionCoversDecimal =
            selectionStart <= existingDecimalIndex && selectionEnd > existingDecimalIndex;

        if (!selectionCoversDecimal) {
            // Already have a decimal somewhere else → ignore this key
            event.preventDefault();
        }
    };

    defineExpose({
        blur: () => inputRef.value?.blur(),
        focus: () => inputRef.value?.focus(),
    });

    watch(() => props.value, syncFromValue, { immediate: true });

    watch(
        () => localValue.value,
        (newValue, oldValue) => {
            if (syncingFromValue.value) {
                return;
            }

            const cleanedValue = normalizeNumericValue(newValue);
            const previousNumericValue = normalizeNumericValue(oldValue);
            const emitValue = standardizeEmitValue(cleanedValue);

            if (cleanedValue !== previousNumericValue) {
                emit('input', emitValue);
                emit('change', emitValue);
            }
        }
    );
</script>
